import {type IDBPDatabase, openDB} from 'idb';


export const IDBTables = {
  configs: 'configs',
  user: 'user',
  logs: 'logs',
} as const;

export type IDBTablesKeys = keyof typeof IDBTables;

export class IDBService {
  private readonly name: string;
  private readonly promise: Promise<IDBPDatabase>;

  constructor(name: string) {
    this.name = name;
    this.promise = this.init();
  }

  // DB 초기화
  private async init(): Promise<IDBPDatabase> {
    return openDB(this.name, 1, {
      upgrade(db) {
        for (const storeName of Object.values(IDBTables)) {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName, { keyPath: '_id', autoIncrement: true });
          }
        }
      },
    });
  }

  // 저장 (추가 또는 수정)
  async save<T>(store: string, value: T): Promise<void> {
    const db = await this.promise;
    await db.put(store, value);
  }

  // 추가 (키 중복 시 에러)
  async add<T>(store: string, value: T): Promise<IDBValidKey> {
    const db = await this.promise;
    return await db.add(store, value);
  }

  // 가져오기
  async get<T>(store: string, id: IDBValidKey): Promise<T | undefined> {
    const db = await this.promise;
    return db.get(store, id);
  }

  // 삭제
  async delete(store: string, id: IDBValidKey): Promise<void> {
    const db = await this.promise;
    await db.delete(store, id);
  }

  // 전체 가져오기
  async getAll<T>(store: string): Promise<T[]> {
    const db = await this.promise;
    return db.getAll(store);
  }

  // 존재 여부
  async has(store: string, id: IDBValidKey): Promise<boolean> {
    const db = await this.promise;
    const value = await db.get(store, id);
    return !!value;
  }

  // 전체 삭제
  async clear(store: string): Promise<void> {
    const db = await this.promise;
    await db.clear(store);
  }

  async getByField<T extends object>(
    store: string,
    field: string,
    value: IDBValidKey
  ): Promise<T | undefined> {
    const db = await this.promise;
    const tx = db.transaction(store);
    const objectStore = tx.store;

    if (objectStore.indexNames.contains(`by_${String(field)}`)) {
      const index = objectStore.index(`by_${String(field)}`);
      const result = await index.get(value); // 이제 OK
      return result as T | undefined;
    }

    const all = await objectStore.getAll();
    return all.find((item) => item[field] === value) as T | undefined;
  }
}